import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createChatApi } from '../../api/chats.api';
import { updateProjectFeaturesApi } from '../../api/features.api';
import { DEFAULT_LLM_MODEL_KEY } from '../../constants/llmModels';
import { getNextTaskTitle } from '../../utils/taskTitles';
import {
  createProjectApi,
  deleteProjectApi,
  fetchProjectByIdApi,
  fetchProjectsApi,
  saveProjectFilesApi,
  updateProjectApi
} from '../../api/projects.api';

export const fetchProjects = createAsyncThunk('projects/fetchProjects', async () => {
  return fetchProjectsApi();
});

export const createProject = createAsyncThunk('projects/createProject', async (payload, { getState }) => {
  const project = await createProjectApi(payload);
  const language = getState().app?.language || 'fa';

  try {
    await createChatApi(project.id, {
      title: getNextTaskTitle(language, 0),
      context: project.description,
      model: payload.model || DEFAULT_LLM_MODEL_KEY
    });
  } catch (error) {
    console.warn(error?.message || 'Default task creation failed.');
  }

  return project;
});

export const fetchProjectById = createAsyncThunk('projects/fetchProjectById', async (projectId) => {
  return fetchProjectByIdApi(projectId);
});

export const updateProject = createAsyncThunk('projects/updateProject', async (payload) => {
  return updateProjectApi(payload);
});

export const saveProjectFiles = createAsyncThunk('projects/saveProjectFiles', async ({ projectId, files }) => {
  return saveProjectFilesApi({ projectId, files });
});

export const updateProjectFeatures = createAsyncThunk('projects/updateProjectFeatures', async ({ projectId, features }) => {
  return updateProjectFeaturesApi({ projectId, features });
});

export const deleteProject = createAsyncThunk('projects/deleteProject', async (projectId) => {
  return deleteProjectApi(projectId);
});

const projectsSlice = createSlice({
  name: 'projects',
  initialState: {
    activeTab: 'your',
    selectedProjectId: null,
    items: [],
    loading: false,
    detailLoading: false,
    hasLoaded: false,
    error: null,
    search: ''
  },
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setSelectedProjectId: (state, action) => {
      state.selectedProjectId = action.payload;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.hasLoaded = true;
        state.items = action.payload;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.hasLoaded = true;
        state.error = action.error.message || 'Project list request failed.';
      })
      .addCase(fetchProjectById.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.error = null;
        const existingIndex = state.items.findIndex((item) => item.id === action.payload.id);

        if (existingIndex >= 0) {
          state.items[existingIndex] = action.payload;
        } else {
          state.items.push(action.payload);
        }
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.error.message || 'Project request failed.';
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.error = null;
        state.items.unshift(action.payload);
      })
      .addCase(createProject.rejected, (state, action) => {
        state.error = action.error.message || 'Create project failed.';
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.error = null;
        state.items = state.items.map((item) =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item
        );
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.error = action.error.message || 'Update project failed.';
      })
      .addCase(saveProjectFiles.fulfilled, (state, action) => {
        state.error = null;
        state.items = state.items.map((item) =>
          item.id === action.payload.id
            ? {
                ...item,
                ...action.payload,
                files: action.payload.files || item.files,
                previewFiles: action.payload.previewFiles || action.payload.files || item.previewFiles,
                updatedAt: new Date().toISOString()
              }
            : item
        );
      })
      .addCase(saveProjectFiles.rejected, (state, action) => {
        state.error = action.error.message || 'Update project files failed.';
      })
      .addCase(updateProjectFeatures.fulfilled, (state, action) => {
        state.error = null;
        const existingIndex = state.items.findIndex((item) => item.id === action.payload.id);

        if (existingIndex >= 0) {
          state.items[existingIndex] = action.payload;
        } else {
          state.items.push(action.payload);
        }
      })
      .addCase(updateProjectFeatures.rejected, (state, action) => {
        state.error = action.error.message || 'Update project features failed.';
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.error = null;
        state.items = state.items.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.error = action.error.message || 'Delete project failed.';
      });
  }
});

export const { setActiveTab, setSelectedProjectId, setSearch } = projectsSlice.actions;
export default projectsSlice.reducer;
